import type { Feature, FeatureCollection, Point } from 'geojson';

export type OffMarketListingProperties = {
  listing_id: string;
  building_id: string | null | undefined;
  could_sell_count?: number;
  could_buy_count?: number;
  // Last time the listing was created or edited (the backend's `updated_at`).
  // Same field the listing card's "New" chip reads, so the two agree.
  activity_at?: string | null;
  isRecentlyCommented?: boolean;
  like_count?: number;
  commentsCount?: number;
  // Compact quality signals emitted by /off-market-listings-in-bounds for the
  // marker tiering below. Absent on features from older responses; those
  // default to the full-pill tier so nothing silently downgrades to a dot.
  has_image?: boolean;
  has_description?: boolean;
  spec_count?: number;
};

export type SellMarkerTier = 'full' | 'dot';

export type OffMarketBuildingProperties = {
  building_id: string;
  could_sell_count: number;
  could_buy_count: number;
  listing_count: number;
  // True when the building has a *sell* listing activated inside the window.
  // Sell-only by design — see the `recentSell` note in aggregateBuildingFeatures.
  is_new: boolean;
  isRecentlyCommented: boolean;
  like_count: number;
  commentsCount: number;
  // Marker prominence, from the building's best sell listing. `full` renders
  // the house pill; `dot` renders the small white dot (JAA sell-marker
  // decluttering). Buildings without sell listings carry the worst rank but
  // never render either way (both layers filter on could_sell_count > 0).
  sell_tier: SellMarkerTier;
  // Mapbox symbol-sort-key: lower = placed first = survives collision.
  sell_priority: number;
};

export type OffMarketListingFeature = Feature<Point, OffMarketListingProperties>;
export type OffMarketBuildingFeature = Feature<Point, OffMarketBuildingProperties>;

const NEW_LISTING_WINDOW_DAYS = 7;
const NEW_LISTING_WINDOW_MS = NEW_LISTING_WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Tuning knobs for the sell-marker tiering. The exact weighting between
// recency, content richness, and other potential signals was deliberately left
// open in the design meeting — adjust here, everything else derives from it.
export const SELL_MARKER_TIERING = {
  // A sell listing is "rich" — kept as a full house pill — when it has at
  // least one image AND a description AND this many filled spec fields.
  RICH_MIN_SPEC_COUNT: 1,
  // symbol-sort-key bands. New listings always outrank rich ones, which
  // always outrank dots; the within-band spread below never crosses a band.
  BAND_NEW: 0,
  BAND_RICH: 1000,
  BAND_DOT: 2000,
  // Within a band, content completeness dominates recency.
  QUALITY_WEIGHT: 600,
  RECENCY_WEIGHT: 300,
  // Age at which the recency contribution stops growing.
  RECENCY_HORIZON_DAYS: 90,
  // Spec count at which the quality contribution saturates.
  QUALITY_SPEC_CAP: 4,
} as const;

const RECENCY_HORIZON_MS =
  SELL_MARKER_TIERING.RECENCY_HORIZON_DAYS * 24 * 60 * 60 * 1000;

// Worst possible rank; also the sentinel for buildings without sell listings.
export const SELL_PRIORITY_MAX =
  SELL_MARKER_TIERING.BAND_DOT +
  SELL_MARKER_TIERING.QUALITY_WEIGHT +
  SELL_MARKER_TIERING.RECENCY_WEIGHT;

const isRecent = (activityAt: string | null | undefined, nowMs: number): boolean => {
  if (!activityAt) return false;
  const ts = Date.parse(activityAt);
  if (Number.isNaN(ts)) return false;
  return nowMs - ts <= NEW_LISTING_WINDOW_MS;
};

// Rank a single sell listing for marker prominence. Lower priority = more
// prominent (it doubles as the Mapbox symbol-sort-key). `isNew` is passed in
// so the band always agrees with the building-level `is_new` badge.
const sellListingPriority = (
  props: OffMarketListingProperties,
  isNew: boolean,
  nowMs: number,
): number => {
  const {
    RICH_MIN_SPEC_COUNT,
    BAND_NEW,
    BAND_RICH,
    BAND_DOT,
    QUALITY_WEIGHT,
    RECENCY_WEIGHT,
    QUALITY_SPEC_CAP,
  } = SELL_MARKER_TIERING;

  // Features from responses predating the quality signals carry none of the
  // three fields. Treat those as full pills so older payloads (and other
  // endpoints reusing this aggregation) keep today's behavior.
  const hasSignals =
    props.has_image !== undefined ||
    props.has_description !== undefined ||
    props.spec_count !== undefined;

  const hasImage = props.has_image === true;
  const hasDescription = props.has_description === true;
  const specCount = Number(props.spec_count) || 0;
  const rich =
    hasImage && hasDescription && specCount >= RICH_MIN_SPEC_COUNT;

  const band = isNew
    ? BAND_NEW
    : rich || !hasSignals
      ? BAND_RICH
      : BAND_DOT;

  // Quality in [0, 1], 1 = best: image, description, and specs weigh equally.
  const quality =
    (Number(hasImage) +
      Number(hasDescription) +
      Math.min(specCount, QUALITY_SPEC_CAP) / QUALITY_SPEC_CAP) /
    3;

  // Age in [0, 1], 0 = fresh. Unknown activity ranks as oldest.
  let age = 1;
  if (props.activity_at) {
    const ts = Date.parse(props.activity_at);
    if (!Number.isNaN(ts)) {
      age = Math.min(Math.max(nowMs - ts, 0) / RECENCY_HORIZON_MS, 1);
    }
  }

  return band + (1 - quality) * QUALITY_WEIGHT + age * RECENCY_WEIGHT;
};

const tierFromPriority = (priority: number): SellMarkerTier =>
  priority < SELL_MARKER_TIERING.BAND_DOT ? 'full' : 'dot';

export const aggregateBuildingFeatures = (
  collection: FeatureCollection | null | undefined,
  nowMs: number = Date.now(),
): FeatureCollection<Point, OffMarketBuildingProperties> => {
  const empty: FeatureCollection<Point, OffMarketBuildingProperties> = {
    type: 'FeatureCollection',
    features: [],
  };
  if (!collection || !Array.isArray(collection.features)) return empty;

  const groups = new Map<string, OffMarketBuildingFeature>();

  for (const raw of collection.features) {
    if (!raw || raw.geometry?.type !== 'Point') continue;
    const props = (raw.properties || {}) as OffMarketListingProperties;
    const buildingId = props.building_id;
    if (buildingId === null || buildingId === undefined || buildingId === '') continue;

    const key = String(buildingId);
    const sell = Number(props.could_sell_count) || 0;
    const buy = Number(props.could_buy_count) || 0;
    // Sell listings only: `is_new` drives the seller pill alone (the buy layer
    // has no `-new` icon variant), so counting a fresh buyer here would badge
    // the building's old seller as "New".
    const recentSell = sell > 0 && isRecent(props.activity_at, nowMs);
    // Ranking is likewise sell-only; buy-only docs never affect the marker
    // tier (their building may still show a pill through another sell doc).
    const priority =
      sell > 0 ? sellListingPriority(props, recentSell, nowMs) : SELL_PRIORITY_MAX;

    const existing = groups.get(key);
    if (existing) {
      existing.properties.could_sell_count += sell;
      existing.properties.could_buy_count += buy;
      existing.properties.listing_count += 1;
      if (recentSell) existing.properties.is_new = true;
      // The building takes its best (lowest) sell listing's rank.
      if (priority < existing.properties.sell_priority) {
        existing.properties.sell_priority = priority;
        existing.properties.sell_tier = tierFromPriority(priority);
      }
    } else {
      groups.set(key, {
        type: 'Feature',
        id: key,
        geometry: raw.geometry as Point,
        properties: {
          building_id: key,
          could_sell_count: sell,
          could_buy_count: buy,
          listing_count: 1,
          is_new: recentSell,
          isRecentlyCommented: Boolean(props.isRecentlyCommented),
          like_count: Number(props.like_count) || 0,
          commentsCount: Number(props.commentsCount) || 0,
          sell_tier: tierFromPriority(priority),
          sell_priority: priority,
        },
      });
    }
  }

  return { type: 'FeatureCollection', features: Array.from(groups.values()) };
};
